// IndexedDB storage for 4D Tetris
// Persists high scores, settings, and saved game state

export interface HighScore {
  id?: number;
  score: number;
  level: number;
  linesCleared: number;
  date: string;
  playerName: string;
}

export interface GameSettings {
  musicVolume: number;
  sfxVolume: number;
  showGhost: boolean;
  showGridLines: boolean;
  cameraShake: boolean;
}

export interface SavedGame {
  id: string;
  gridState: string; // JSON serialized grid
  score: number;
  level: number;
  linesCleared: number;
  currentW: number;
  date: string;
}

const DB_NAME = '4DTetrisDB';
const DB_VERSION = 1;

export class GameStorage {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void>;

  constructor() {
    this.initPromise = this.init();
  }

  private init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // High scores store
        if (!db.objectStoreNames.contains('highScores')) {
          const highScoresStore = db.createObjectStore('highScores', {
            keyPath: 'id',
            autoIncrement: true,
          });
          highScoresStore.createIndex('score', 'score', { unique: false });
          highScoresStore.createIndex('date', 'date', { unique: false });
        }

        // Settings store
        if (!db.objectStoreNames.contains('settings')) {
          db.createObjectStore('settings', { keyPath: 'key' });
        }

        // Saved games store
        if (!db.objectStoreNames.contains('savedGames')) {
          const savedGamesStore = db.createObjectStore('savedGames', {
            keyPath: 'id',
          });
          savedGamesStore.createIndex('date', 'date', { unique: false });
        }
      };
    });
  }

  private async ensureReady(): Promise<IDBDatabase> {
    await this.initPromise;
    if (!this.db) {
      throw new Error('Database not initialized');
    }
    return this.db;
  }

  // High Scores
  async addHighScore(score: Omit<HighScore, 'id'>): Promise<number> {
    const db = await this.ensureReady();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['highScores'], 'readwrite');
      const store = transaction.objectStore('highScores');
      const request = store.add(score);

      request.onsuccess = () => resolve(request.result as number);
      request.onerror = () => reject(request.error);
    });
  }

  async getTopScores(limit: number = 10): Promise<HighScore[]> {
    const db = await this.ensureReady();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['highScores'], 'readonly');
      const store = transaction.objectStore('highScores');
      const index = store.index('score');
      const request = index.openCursor(null, 'prev');

      const scores: HighScore[] = [];
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor && scores.length < limit) {
          scores.push(cursor.value);
          cursor.continue();
        } else {
          resolve(scores);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  async isHighScore(score: number): Promise<boolean> {
    const topScores = await this.getTopScores(10);
    if (topScores.length < 10) return true;
    return score > topScores[topScores.length - 1].score;
  }

  // Settings
  async saveSetting<K extends keyof GameSettings>(
    key: K,
    value: GameSettings[K]
  ): Promise<void> {
    const db = await this.ensureReady();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['settings'], 'readwrite');
      const store = transaction.objectStore('settings');
      const request = store.put({ key, value });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getSetting<K extends keyof GameSettings>(
    key: K
  ): Promise<GameSettings[K] | null> {
    const db = await this.ensureReady();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['settings'], 'readonly');
      const store = transaction.objectStore('settings');
      const request = store.get(key);

      request.onsuccess = () => {
        resolve(request.result?.value ?? null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getAllSettings(): Promise<Partial<GameSettings>> {
    const db = await this.ensureReady();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['settings'], 'readonly');
      const store = transaction.objectStore('settings');
      const request = store.getAll();

      request.onsuccess = () => {
        const settings: Partial<GameSettings> = {};
        for (const item of request.result) {
          (settings as Record<string, unknown>)[item.key] = item.value;
        }
        resolve(settings);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Saved Games
  async saveGame(game: SavedGame): Promise<void> {
    const db = await this.ensureReady();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['savedGames'], 'readwrite');
      const store = transaction.objectStore('savedGames');
      const request = store.put(game);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async loadGame(id: string): Promise<SavedGame | null> {
    const db = await this.ensureReady();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['savedGames'], 'readonly');
      const store = transaction.objectStore('savedGames');
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async getSavedGames(): Promise<SavedGame[]> {
    const db = await this.ensureReady();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['savedGames'], 'readonly');
      const store = transaction.objectStore('savedGames');
      const index = store.index('date');
      const request = index.openCursor(null, 'prev');

      const games: SavedGame[] = [];
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          games.push(cursor.value);
          cursor.continue();
        } else {
          resolve(games);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  async deleteGame(id: string): Promise<void> {
    const db = await this.ensureReady();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['savedGames'], 'readwrite');
      const store = transaction.objectStore('savedGames');
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Auto-save slot (quick save/load)
  async quickSave(game: Omit<SavedGame, 'id' | 'date'>): Promise<void> {
    await this.saveGame({
      ...game,
      id: 'autosave',
      date: new Date().toISOString(),
    });
  }

  async quickLoad(): Promise<SavedGame | null> {
    return this.loadGame('autosave');
  }

  async hasQuickSave(): Promise<boolean> {
    const save = await this.loadGame('autosave');
    return save !== null;
  }
}

// Singleton instance
export const gameStorage = new GameStorage();
